<ng-container *ngIf="(authorizations$ | async)?.length > 0; else noAuthorizations">
  @let authorizations = authorizationsToShow$ | async;
  <div class="p-3" fxLayout="row wrap" fxLayoutAlign="start center" fxLayoutGap="12px">
    <mat-form-field appearance="outline" subscriptSizing="dynamic" style="min-width: 320px;">
      <mat-label>{{ 'patient-details.authorization-card.filters.cpt-label' | translate }}</mat-label>
      <input
        matInput
        [placeholder]="'patient-details.authorization-card.filters.cpt-placeholder' | translate"
        [formControl]="filtersForm.controls.cpt">
    </mat-form-field>

    <button
      *ngIf="hasActiveFilters$ | async"
      mat-stroked-button
      (click)="clearFilters()">
      {{ 'patient-details.authorization-card.filters.clear' | translate }}
    </button>
  </div>

  <ng-container *ngIf="authorizations?.length > 0; else noFilteredAuthorizations">
    <table mat-table [dataSource]="authorizations">
      <ng-container matColumnDef="effectiveDate">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'patient-details.authorization-card.effective-date' | translate }}
        </th>
        <td mat-cell *matCellDef="let authorization">
          <div *ngIf="authorization.startAt || authorization.endAt" class="nowrap" fxFlexAlign="start start" fxLayout="column" fxLayoutGap="5px">
            <span class="pr-1">
              {{ authorization.startAt | momentDate : 'utc' }}
              &mdash;
              {{ authorization.endAt | momentDate : 'utc' }}
            </span>    
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="insurance">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'common.insurance' | translate }}
        </th>
        <td mat-cell *matCellDef="let authorization">
          <ng-container *ngIf="authorization.carrierLookup; else lookupInsuranceEl">
            <span [matTooltip]="authorization.carrierLookup?.clinicCarrierId">
              {{ authorization.carrierLookup?.clinicCarrierName }}
            </span>
          </ng-container>
          <ng-template #lookupInsuranceEl>
            <ng-container *ngIf="getInsurance(authorization) as insurance">
              {{ insurance.clinicCarrierName || insurance.clinicCarrierId }}
            </ng-container>
          </ng-template>
        </td>
      </ng-container>

      <ng-container matColumnDef="number">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'patient-details.authorization-card.table.numbers' | translate }}
        </th>
        <td mat-cell *matCellDef="let authorization">
          <div fxLayout="column" fxLayoutGap="5px">
            <div *ngIf="authorization.number" [matTooltip]="'patient-details.authorization-card.table.certification-number' | translate">
              <!-- {{ 'patient-details.authorization-card.table.certification-number' | translate }}: -->
              <mmx-copy-text>{{ authorization.number }}</mmx-copy-text>
            </div>
            <div *ngIf="authorization.referenceNumber" [matTooltip]="'patient-details.authorization-card.table.reference-number' | translate">
              <!-- {{ 'patient-details.authorization-card.table.reference-number' | translate }}: -->
              <mmx-copy-text>{{ authorization.referenceNumber }}</mmx-copy-text>
            </div>
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="referenceNumber">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'patient-details.authorization-card.table.reference-number' | translate }}
        </th>
        <td mat-cell *matCellDef="let authorization">
        </td>
      </ng-container>

      <ng-container matColumnDef="authorizationStatusText">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'common.status' | translate }}
        </th>
        <td mat-cell *matCellDef="let authorization">
          <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="10px">
            <span *ngIf="authorization.status" class="nowrap">
              {{ ('statuses.authorizations.' + authorization.status) | translate }}
              <ng-container *ngIf="authorization.awaitingAsyncPullResult">
                <br> <i>{{'statuses.authorizations.statusing' | translate}}</i>
              </ng-container>
            </span>

            <ng-container *ngIf="isOutdated(authorization)">
              <button
                *appHasPermission="Permission.AUTHORIZATION_EDIT"
                mat-icon-button
                (click)="editAuthorization.emit(authorization)">
                <mat-icon color="warn" [matTooltip]="'patient-details.authorization-card.outdated' | translate">warning</mat-icon>
              </button>
            </ng-container>
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="procedures">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'patient-details.authorization-card.table.procedures' | translate }}
        </th>
        <td mat-cell *matCellDef="let authorization">
          <div fxLayout="row" fxLayoutAlign="start center">
            <div fxFlex>
              <ng-container *ngFor="let code of authorization.procedureCodesDisplay">
                <div class="procedure-row">
                  <ng-container  *ngIf="showAuthorizationExpiringSoon(authorization)"> 
                    <mat-icon matTooltip="{{ getAuthorizationExpirationLabel(authorization) | translate : getAuthorizationExpirationParams(authorization) }}" class="mat-icon" color="warn">warning</mat-icon>
                  </ng-container>
                  <div class="procedure nowrap">
                    {{ code }}
                    <ng-container *ngIf="authorization.proceduresData?.[code] as procedureData">
                    <span
                      class="procedure-quantity"
                      [ngClass]='{"not-applicable": procedureData.quantityNotApplicable}'
                      *ngIf="showProcedureQuantity"
                      [matTooltip]="('common.remaining' | translate) + ': ' + (procedureData.quantityRemaining != null ? (procedureData.quantityRemaining < 0 ? 0 : procedureData.quantityRemaining): (procedureData.quantity ?? 0))"
                      [matTooltipDisabled]="procedureData.quantityNotApplicable || !showRemaining"
                    >
                      ({{'common.qty' | translate}}:
                      {{ procedureData.quantity ?? 0 }})
                    </span>
                      <ng-container *ngIf="procedureData.statusCode">
                        - {{ ('statuses.authorization-procedures.' + procedureData.statusCode) | translate }}
                      </ng-container>
                    </ng-container>
                  </div>
                </div>
              </ng-container>
            </div>
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="diagnoses">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'patient-details.authorization-card.table.diagnoses' | translate }}
        </th>
        <td mat-cell *matCellDef="let authorization">
          <div *ngIf="authorization.diagnosisCodes?.length" fxLayout="column" fxLayoutGap="5px">
            <div *ngFor="let code of authorization.diagnosisCodes" class="nowrap">
              {{ code }}
            </div>
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="facility">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'patient-details.authorization-card.table.facility-npi' | translate }}
        </th>
        <td mat-cell *matCellDef="let authorization">
          <div fxLayout="row">
            {{ authorization.facilityNPI }}
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="note">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'common.notes' | translate }}
        </th>
        <td mat-cell *matCellDef="let authorization" [style.white-space]="'pre-line'">
          @if (authorization.manualNotes) {
            <div>
              <b>{{'patient-details.authorization-card.table.manual-auth-note.title' | translate}}</b>
            </div>
            {{ authorization.manualNotes }}
            <br>
            <br>
            @if (authorization.note) {
              <div>
                <b>{{'patient-details.authorization-card.table.note.title' | translate}}</b>
              </div>
            }
          }

          {{ authorization.note }}
        </td>
      </ng-container>

      <ng-container matColumnDef="edit">
        <th mat-header-cell *matHeaderCellDef>
        </th>
        <td mat-cell *matCellDef="let authorization">
          <button
            mat-icon-button
            [matMenuTriggerFor]="menu"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #menu="matMenu">
            <ng-container *ngIf="authorization.awaitingAsyncPullResult">
              <button
                *appHasPermission="Permission.AUTHORIZATION_EDIT"
                mat-menu-item
                (click)="cancelPullAuthStatus(authorization)">
                <mat-icon color="warn">close</mat-icon>
                <span>{{ 'patient-details.authorization-card.pull-status-cancel' | translate }}</span>
              </button>
            </ng-container>

            <ng-container *ngIf="canPullStatus(authorization) as canPull">
              <button
                *appHasPermission="Permission.AUTHORIZATION_EDIT"
                mat-menu-item
                [disabled]="!canPull.result"
                [matTooltip]="getPullStatusTooltip(canPull)"
                (click)="pullAuthStatus(authorization)">
                <mat-icon>download</mat-icon>
                <span>{{ 'patient-details.authorization-card.pull-status' | translate }}</span>
              </button>
            </ng-container>
            <button
              *appHasPermission="Permission.AUTHORIZATION_EDIT"
              mat-menu-item
              (click)="editAuthorization.emit(authorization)"
            >
              <mat-icon>edit</mat-icon>
              <span>{{ 'patient-details.authorization-card.button-edit' | translate }}</span>
            </button>
            <!-- <ng-container *appHasPermission="Permission.AUTHORIZATION_EDIT">
              <button
                *ngIf="isAuthSubmissionSupported(authorization)"
                mat-menu-item
                (click)="openSubmitToPayerDialog(patient, authorization)"
              >
                <mat-icon>upload</mat-icon>
                <span>{{ 'patient-details.authorization-card.table.submit-to-payer' | translate }}</span>
              </button>
            </ng-container> -->
            <button
              *ngIf="authorization.fullAuthorizationObject"
              mat-menu-item (click)="openRawResponse(authorization)">
              <mat-icon>code</mat-icon>
              <span>{{ 'patient-details.eligibility-card.view-raw-response' | translate }}</span>
            </button>
            <button
              mat-menu-item
              (click)="showHistory(authorization)">
              <mat-icon>history</mat-icon>
              <span>{{ 'common.history' | translate }}</span>
            </button>
            <button
              mat-menu-item
              [matMenuTriggerFor]="attachmentsMenu"
              [disabled]="!authorization.attachments?.length"
            >
              <mat-icon>attach_file</mat-icon>
              <span>{{ 'patient-details.authorization-card.table.attachments' | translate }}</span>
            </button>
            <ng-container *appHasPermission="'authorization.delete'">
              <mat-divider></mat-divider>
              <button mat-menu-item mmxConfirmClick message="Are you sure you want to delete this authorization?"
                (confirm)="deleteAuthorization.emit(authorization)">
                <mat-icon color="warn">delete</mat-icon>
                {{ 'common.delete' | translate }}
              </button>
            </ng-container>
          </mat-menu>

          <mat-menu #attachmentsMenu="matMenu">
            <button
              *ngFor="let attachment of authorization.attachments"
              mat-menu-item
              (click)="viewFile(authorization, attachment)"
            >
              {{ attachment.fileName }}
            </button>
          </mat-menu>
        </td>
      </ng-container>

      <ng-container matColumnDef="appointment">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let authorization">
          <span [matTooltip]="getCheckboxLabel(authorization) | translate">
            <mat-checkbox
              [disabled]="isCheckboxDisabled(authorization)"
              [checked]="checkboxChecked(authorization)"
              (change)="checkboxChanged(authorization)">
            </mat-checkbox>
          </span>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
    </table>
  </ng-container>
</ng-container>

<ng-template #noAuthorizations>
  <div class="mmx-card-padding">
    {{ 'patient-details.authorization-card.no-authorization' | translate }}
  </div>
</ng-template>

<ng-template #noFilteredAuthorizations>
  <div class="mmx-card-padding">
    {{ 'patient-details.authorization-card.filters.no-matches' | translate }}
  </div>
</ng-template>
